import { normalizedText } from './normalized-text.js'

/**
 * **Onde** um preço foi visto: uma loja física ou um link.
 *
 * ## Os dois casos são o mesmo campo, e isso é o desenho
 *
 * *"Pode ser um link (por exemplo, um link da Amazon) ou até mesmo um local,
 * tipo mercado Walmart Campo Comprido."* São a mesma pergunta — onde eu vi
 * isto —, e separar em dois campos faria a comparação *"onde este produto
 * costuma ser mais barato"* olhar só metade dos preços.
 *
 * O link **também tem loja**: `amazon.com.br/dp/B0...` é a Amazon. Guardar só a
 * URL faria cada link virar um lugar diferente, e três compras na Amazon
 * apareceriam como três lojas de uma compra cada. Daí o host virar o nome, e a
 * URL ficar guardada junto — ela diz **qual página**, que é o que permite voltar
 * lá e conferir.
 *
 * ## `Merchant` é entidade com id, pela terceira vez neste projeto
 *
 * "Walmart Campo Comprido" e "walmart campo comprido" precisam ser o mesmo
 * lugar. Um campo de texto solto faria a mesma loja virar duas na primeira vez
 * que alguém digitasse com pressa — o mesmo erro que `ProductMatch.md` chama de
 * *"o erro clássico que arruína catálogos"*, e o mesmo motivo pelo qual
 * `ProductFamily` também é entidade (decisão 181).
 *
 * A chave é `normalizedText`, a mesma do catálogo e do matching.
 *
 * ## O que NÃO foi construído do `Merchant.md`
 *
 * `patterns` e `defaultCategoryId` entraram com a decisão 195, quando aquele
 * caminho passou a existir: a tela de classificação por estabelecimento agrupa
 * o extrato por `merchantKeyOf` e grava aqui o que foi decidido, para o mês
 * seguinte já vir sugerido.
 *
 * Continuam de fora `displayName`, `logoUrl`, `transactionCount`, `lastSeenAt`
 * e `cnpj` — campos que ninguém leria ainda.
 */

export type Merchant = {
  id: string
  householdId: string
  /** "Amazon", "Walmart Campo Comprido". */
  name: string
  /** A chave que impede a mesma loja de virar duas. */
  normalizedName: string
  /**
   * As chaves de descrição bancária que resolvem para esta loja —
   * `merchantKeyOf` de `domain/finance`.
   *
   * Separado de `normalizedName` porque respondem a perguntas diferentes:
   * `normalizedName` impede que a pessoa crie a mesma loja duas vezes
   * digitando; `patterns` é o que o extrato diz. `Uber Uber *Trip Help.U` vira
   * `uber` aqui e nunca seria digitado por ninguém.
   *
   * Lista, e não campo único: `Merchant.md` prevê fundir duas lojas, e a fusão
   * junta os padrões do absorvido ao sobrevivente.
   */
  patterns: string[]
  /**
   * A categoria que esta loja costuma ter. **Sugestão, não aplicação** —
   * princípio 5, e `Merchant.md` é explícito: *"Sugere, não aplica"*.
   */
  defaultCategoryId: string | null
  createdAt: string
  updatedAt: string
}

export type Onde = {
  /** O nome da loja. `null` quando não deu para extrair nada. */
  merchantName: string | null
  /** A página exata, quando o que veio era um link. */
  sourceUrl: string | null
}

/**
 * Os domínios cujo nome de loja não é o host inteiro. Sem isto,
 * `amazon.com.br` viraria "Amazon.com.br" e `www.amazon.com.br` viraria outra
 * loja — e a comparação passaria a depender de a pessoa ter colado o link com
 * `www` ou sem.
 */
const CONHECIDOS: Record<string, string> = {
  'amazon.com.br': 'Amazon',
  'amazon.com': 'Amazon',
  'mercadolivre.com.br': 'Mercado Livre',
  'americanas.com.br': 'Americanas',
  'magazineluiza.com.br': 'Magazine Luiza',
  'carrefour.com.br': 'Carrefour',
}

/**
 * Interpreta o que a pessoa escreveu no campo "onde".
 *
 * Link vira loja **e** URL; texto vira só loja. Vazio devolve os dois `null` —
 * registrar preço sem dizer onde continua valendo, porque o preço é o dado e o
 * lugar é o contexto.
 */
export function parseOnde(cru: string): Onde {
  const limpo = cru.trim()
  if (limpo === '') return { merchantName: null, sourceUrl: null }

  const url = comoUrl(limpo)
  if (url === null) return { merchantName: limpo, sourceUrl: null }

  return { merchantName: lojaDoHost(url.hostname), sourceUrl: url.toString() }
}

/**
 * `null` quando não é um link.
 *
 * Aceita sem protocolo — quem cola um link do celular às vezes traz
 * `amazon.com.br/dp/...` sem o `https://`. Mas exige um ponto no host: sem
 * isso, "Walmart Campo Comprido" viraria uma URL chamada `walmart`.
 */
function comoUrl(texto: string): URL | null {
  const comProtocolo = /^https?:\/\//i.test(texto) ? texto : `https://${texto}`
  try {
    const url = new URL(comProtocolo)
    if (!url.hostname.includes('.')) return null
    if (/\s/.test(texto)) return null
    return url
  } catch {
    return null
  }
}

/** `www.amazon.com.br` → `Amazon`. O que não é conhecido vira o host sem `www`. */
function lojaDoHost(hostname: string): string {
  const semWww = hostname.replace(/^www\./i, '').toLowerCase()
  return CONHECIDOS[semWww] ?? semWww
}

/**
 * A loja existente que casa com este nome, ou `null`.
 *
 * Compara pelo **normalizado**, que é o que faz "Walmart Campo Comprido" e
 * "walmart campo comprido" serem a mesma.
 */
export function findMerchant(name: string, merchants: Merchant[]): Merchant | null {
  const chave = normalizedText(name)
  if (chave === '') return null
  return merchants.find((m) => m.normalizedName === chave) ?? null
}
